"use client";

// The ONE <VideoPlayer> of the whole app. Mounted once in the locale layout,
// never unmounted on navigation → audio keeps playing across routes.
// Mini mode: the player is kept in the DOM but visually hidden (plays audio);
// Full mode: the same node is promoted to a full-screen sheet.

import { useEffect, useMemo } from "react";
import { useTranslations } from "next-intl";
import { ChevronDown } from "lucide-react";

import { VideoPlayer } from "@/components/video-player/player";
import type { VideoPlayerAsset } from "@/components/video-player/player";
import { usePlayerStore } from "@/stores/player-store";
import { guessMimeType, type PlayerAsset } from "@/stores/player-types";
import { createProgressiveLoader } from "@/lib/progressive-loader";
import MiniPlayer from "./MiniPlayer";
import PlayerBridge from "./PlayerBridge";

function toVideoAsset(a: PlayerAsset): VideoPlayerAsset {
  return {
    id: a.id,
    src: a.src,
    mimeType: a.mimeType ?? guessMimeType(a.src),
    title: a.title,
    description: a.artist, // → Media Session "artist"
    poster: a.poster, // → Media Session artwork + <video poster>; null → fallback-poster
  };
}

const hiddenStyle: React.CSSProperties = {
  position: "fixed",
  width: 1,
  height: 1,
  overflow: "hidden",
  opacity: 0,
  pointerEvents: "none",
  insetInlineStart: 0,
  bottom: 0,
};

export default function UnifiedPlayer() {
  const t = useTranslations("player");
  const current = usePlayerStore((s) => s.current);
  const loadRequested = usePlayerStore((s) => s.loadRequested);
  const expanded = usePlayerStore((s) => s.expanded);
  const setExpanded = usePlayerStore((s) => s.setExpanded);
  const resolveSource = usePlayerStore((s) => s.resolveSource);

  // Restore last-played media (paused, with cover) once on the client.
  useEffect(() => {
    void usePlayerStore.persist.rehydrate();
  }, []);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setExpanded(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded, setExpanded]);

  const source = useMemo(
    () => (current ? toVideoAsset(current) : undefined),
    [current],
  );
  const loading = useMemo(
    () => ({
      resolveSource,
      loader: createProgressiveLoader(resolveSource),
    }),
    [resolveSource],
  );

  return (
    <>
      <div
        dir="ltr" // player chrome is always LTR
        aria-hidden={!expanded}
        style={
          expanded
            ? {
                position: "fixed",
                inset: 0,
                zIndex: 70,
                display: "flex",
                flexDirection: "column",
                background: "#000",
              }
            : hiddenStyle
        }
      >
        {expanded && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            aria-label={t("collapse")}
            style={{
              alignSelf: "flex-start",
              margin: 8,
              padding: 8,
              borderRadius: 999,
              border: 0,
              color: "#fff",
              background: "rgba(255,255,255,0.14)",
              cursor: "pointer",
            }}
          >
            <ChevronDown size={22} />
          </button>
        )}
        <div style={{ flex: 1, minHeight: 0, width: "100%" }}>
          <VideoPlayer
            layout="fill"
            theme="dark"
            source={source}
            autoLoad={loadRequested}
            loading={loading}
            mediaProps={{ playsInline: true, poster: current?.poster }}
          >
            <PlayerBridge />
          </VideoPlayer>
        </div>
      </div>
      {!expanded && <MiniPlayer />}
    </>
  );
}
